import { apiClient } from '@/lib/axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface IDailyReportConfig {
  id: string;
  name: string;
  clientCompanyNameForReports: string | null;
  logoUrl: string | null;
  timezone: string | null;
  dailyReportEnabled: boolean;
  dailyReportSendTime: string | null;
  dailyReportTimezone: string | null;
  dailyReportSendDaysOfWeek: string | null;
  dailyReportEmailTemplateId: string | null;
  dailyReportEmailTemplate: { id: string; code: string; name: string } | null;
  contacts: Array<{
    id: string;
    firstName: string;
    surname: string;
    emailAddress: string | null;
    isDailyReportRecipient: boolean;
  }>;
  recentRuns: IReportRun[];
  availableTemplates: Array<{ id: string; code: string; name: string; subject: string; body: string }>;
  whitelistStaleForCurrent: boolean;
}

export interface IAllowedTemplateEntry {
  id: string;
  code: string;
  name: string;
  subject: string;
  body: string;
  allowed: boolean;
  usageCount: number;
  usingClients: Array<{ id: string; name: string }>;
}

export interface IAllowedTemplatesResponse {
  templates: IAllowedTemplateEntry[];
  stale: Array<{ id: string }>;
}

export interface IReportRun {
  id: string;
  reportDate: string;
  scheduledSendAt: string;
  actualSentAt: string | null;
  status: string;
  isTestMode: boolean;
  recipientsActual: string | null;
  recipientsOriginal: string | null;
  staffCount: number | null;
  leaveCount: number | null;
  absentCount: number | null;
  pdfUrl: string | null;
  errorMessage?: string | null;
  retryCount?: number;
  client?: { id: string; name: string; code: string | null };
}

export interface IUpdateDailyReportConfigDto {
  clientCompanyNameForReports?: string;
  dailyReportEnabled?: boolean;
  dailyReportSendTime?: string;
  dailyReportTimezone?: string;
  dailyReportSendDaysOfWeek?: string;
  dailyReportEmailTemplateId?: string | null;
  logoUrl?: string | null;
  recipients?: Array<{ contactId: string; isDailyReportRecipient: boolean }>;
}

export interface IReportRunFilters {
  clientId?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  isTestMode?: boolean;
  page?: number;
  pageSize?: number;
}

export interface ITestModeConfig {
  testModeEnabled: boolean;
  testModeRecipient: string;
}

// ── API Functions ─────────────────────────────────────────────────────────────

export async function getDailyReportConfig(clientId: string): Promise<IDailyReportConfig> {
  const res = await apiClient.get<{ success: boolean; data: IDailyReportConfig }>(`/clients/${clientId}/daily-report-config`);
  return res.data.data;
}

export async function updateDailyReportConfig(clientId: string, dto: IUpdateDailyReportConfigDto): Promise<void> {
  await apiClient.put(`/clients/${clientId}/daily-report-config`, dto);
}

export async function getReportRuns(filters: IReportRunFilters): Promise<{ data: IReportRun[]; total: number; page: number; pageSize: number }> {
  const res = await apiClient.get<{ success: boolean; data: IReportRun[]; total: number; page: number; pageSize: number }>('/report-runs', { params: filters });
  return res.data;
}

export async function resendReportRun(id: string): Promise<void> {
  await apiClient.post(`/report-runs/${id}/resend`);
}

export async function getTestModeConfig(): Promise<ITestModeConfig> {
  const res = await apiClient.get<{ success: boolean; data: ITestModeConfig }>('/system/daily-report-config');
  return res.data.data;
}

export async function updateTestModeConfig(dto: ITestModeConfig): Promise<void> {
  await apiClient.put('/system/daily-report-config', dto);
}

export async function getAllowedTemplatesWithUsage(): Promise<IAllowedTemplatesResponse> {
  const res = await apiClient.get<{ success: boolean; data: IAllowedTemplatesResponse }>('/system/daily-report-config/allowed-templates');
  return res.data.data;
}

export async function updateAllowedEmailTemplateIds(allowedEmailTemplateIds: string[]): Promise<void> {
  await apiClient.put('/system/daily-report-config/allowed-templates', { allowedEmailTemplateIds });
}
